/**
 * Read-only detail modal: full message, anchored cell for thread items, and
 * provenance. Optional Edit hands off to the caller's edit flow.
 */

import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import Link from '@amzn/awsui-components-core/link';
import Modal from '@amzn/awsui-components-core/modal';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import { ActionItemRecord } from 'src/types/actionItems.types';
import { relativeTime } from 'src/components/table/relativeTime';
import { CalloutAuthor } from 'src/components/table/CalloutAuthor';
import { useAppDispatch, useAppSelector } from 'src/app/store';
import { calloutCellSelected } from 'src/features/dashboard/dashboardSlice';
import { useMetricCatalog } from 'src/features/api/useMetricCatalog';
import { toWeekLabel } from 'src/features/api/durationRange';
import { ActionItemStatusIndicator } from './ActionItemStatus';

export interface ActionItemDetailProps {
  record: ActionItemRecord;
  onDismiss: () => void;
  /** Present = an Edit button hands off to the caller's edit flow. */
  onEdit?: () => void;
  /** Off when the caller already sits inside the item's thread. Default on. */
  showThreadLink?: boolean;
}

/** One labelled value in the detail grid. */
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <Box variant="awsui-key-label">{label}</Box>
    <div>{children}</div>
  </div>
);

export const ActionItemDetail: React.FC<ActionItemDetailProps> = ({
  record,
  onDismiss,
  onEdit,
  showThreadLink = true,
}) => {
  const dispatch = useAppDispatch();
  const selected = useAppSelector((s) => s.dashboard.selectedCalloutCell);
  const { catalog, nameOf, groupOf } = useMetricCatalog(record.dashboardId);

  // Select the anchored cell so the callout panel opens on its thread.
  const openThread = () => {
    const ctx = record.context;
    if (!ctx) return;
    // Re-selecting the current cell would only reset the panel's drafts.
    const alreadySelected =
      selected?.dashboardId === record.dashboardId &&
      selected.metricKey === ctx.metricKey &&
      selected.reportingPeriod === ctx.reportingPeriod &&
      selected.metricCategory === ctx.metricCategory;
    if (!alreadySelected) {
      dispatch(
        calloutCellSelected({
          dashboardId: record.dashboardId,
          metricKey: ctx.metricKey,
          reportingPeriod: ctx.reportingPeriod,
          dataMode: 'SNAPSHOT',
          // Empty until the catalog resolves so the panel's own fallback can
          // pick up the name later, instead of pinning the raw key.
          metricName: catalog.has(ctx.metricKey) ? nameOf(ctx.metricKey) : '',
          group: groupOf(ctx.metricKey),
          metricCategory: ctx.metricCategory,
          weekLabel: toWeekLabel(ctx.reportingPeriod),
        }),
      );
    }
    onDismiss();
  };

  return (
    <Modal
      visible={true}
      size="medium"
      onDismiss={onDismiss}
      closeAriaLabel="Close"
      header={record.heading}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {onEdit && (
              <Button onClick={onEdit} data-testid="action-item-detail-edit">
                Edit
              </Button>
            )}
            <Button variant="primary" onClick={onDismiss}>
              Close
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <ColumnLayout columns={3} variant="text-grid">
          <Field label="Status">
            <ActionItemStatusIndicator status={record.status} />
          </Field>
          <Field label="Assignee">
            {record.assignee ? <CalloutAuthor author={record.assignee} /> : '—'}
          </Field>
          <Field label="ETA">{record.eta ?? '—'}</Field>
        </ColumnLayout>
        <Field label="Message">
          {record.message ? (
            <Box data-testid="action-item-detail-message">
              {/* Preserve authored line breaks without markup interpretation. */}
              <span style={{ whiteSpace: 'pre-wrap' }}>{record.message}</span>
            </Box>
          ) : (
            <Box color="text-body-secondary">No message.</Box>
          )}
        </Field>
        {record.type === 'CALLOUT_THREAD' && record.context && (
          <Field label="Callout thread">
            {showThreadLink ? (
              <Link onFollow={openThread} data-testid="action-item-open-thread">
                {nameOf(record.context.metricKey)}
              </Link>
            ) : (
              nameOf(record.context.metricKey)
            )}{' '}
            · {record.context.metricCategory} · {record.context.reportingPeriod}
          </Field>
        )}
        <Box fontSize="body-s" color="text-body-secondary">
          Created by <CalloutAuthor author={record.createdByActor} /> ·{' '}
          {relativeTime(record.createdAt)}
          {' — last updated by '}
          <CalloutAuthor author={record.updatedByActor} /> ·{' '}
          {relativeTime(record.updatedAt)}
        </Box>
      </SpaceBetween>
    </Modal>
  );
};
